import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Clock3, Handshake, Layers, ShieldCheck, UserRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { PORTAL_LINKS } from '../constants/site';

const BENEFITS = [
  { title: 'Access to multiple lenders', text: 'Review lender options that suit different credit profiles and borrowing needs.', icon: Layers },
  { title: 'Faster approvals', text: 'We package your file clearly to reduce delays and improve turnaround speed.', icon: Clock3 },
  { title: 'We handle the process', text: 'You get guided support from enquiry through to lender communication and follow-up.', icon: ShieldCheck },
  { title: 'Better chance of approval', text: 'We match your application to lenders more likely to support your situation.', icon: Handshake },
];

const PROCESS_STEPS = [
  'Share your borrowing goal and preferred repayment range.',
  'We compare lender options and shortlist a best-fit path.',
  'We guide lodgement, lender follow-up, and approval steps.',
];

export default function PersonalLoansPage() {
  return (
    <div className="min-h-screen pt-24" data-testid="personal-loans-page">
      <SEO
        title="Personal Loans Australia | Fast Approvals | Flash Funding"
        description="Looking for a personal loan in Australia? Flash Funding compares options across 40+ lenders to find the right fit for your situation. Apply in 60 seconds."
        keywords="personal loans australia, personal finance, unsecured personal loan"
        canonical="/personal-loans"
      />

      <section className="py-16 sm:py-20" data-testid="personal-loans-hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl rounded-3xl border border-purple-200 bg-white p-8 shadow-xl shadow-purple-200/30 sm:p-10" data-testid="personal-loans-hero-card">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-purple-200 bg-purple-50 px-4 py-2" data-testid="personal-loans-hero-badge">
              <UserRound size={14} className="text-purple-700" />
              <span className="text-xs uppercase tracking-[0.14em] text-purple-700">Personal Loans</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.04] text-[#0F0A1A]" data-testid="personal-loans-hero-heading">
              Personal Loans Australia — We Match You to the Right Lender
            </h1>
            <p className="mt-5 max-w-3xl text-sm text-[#4A4556] sm:text-base" data-testid="personal-loans-hero-subheading">
              Whether you’re consolidating debt or funding personal expenses, we help you compare lender options and move quickly.
            </p>

            <div className="mt-7 flex flex-wrap gap-3" data-testid="personal-loans-hero-cta-row">
              <a href={PORTAL_LINKS.PERSONAL} data-testid="personal-loans-hero-primary-link">
                <Button className="rounded-full bg-purple-600 px-7 py-6 text-xs font-bold uppercase tracking-[0.14em] text-white hover:bg-purple-700" data-testid="personal-loans-hero-primary-cta">
                  Check Your Options
                  <ArrowRight size={15} className="ml-2" />
                </Button>
              </a>
              <a href={PORTAL_LINKS.PERSONAL} data-testid="personal-loans-hero-secondary-link">
                <Button variant="outline" className="rounded-full border-purple-300 bg-purple-50 px-7 py-6 text-xs font-bold uppercase tracking-[0.14em] text-purple-700 hover:bg-purple-100" data-testid="personal-loans-hero-secondary-cta">
                  See What You Can Get Approved For
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-purple-100 bg-white py-14" data-testid="personal-loans-clarity-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="mb-4 text-3xl font-bold text-[#0F0A1A] sm:text-4xl" data-testid="personal-loans-clarity-heading">What Can You Use a Personal Loan For?</h2>
          <p className="mb-4 text-sm leading-relaxed text-[#4A4556] sm:text-base" data-testid="personal-loans-clarity-text-1">
            Personal loans are designed for individual borrowing needs such as debt consolidation, medical costs, travel, or major life expenses.
          </p>
          <p className="text-sm leading-relaxed text-[#4A4556] sm:text-base" data-testid="personal-loans-clarity-text-2">
            Instead of applying lender by lender, we help you compare suitable options and manage the process so you can make a confident decision faster.
          </p>

          <div className="mt-7" data-testid="personal-loans-mid-cta-wrap">
            <a href={PORTAL_LINKS.PERSONAL} data-testid="personal-loans-mid-cta-link">
              <Button className="rounded-full bg-purple-600 px-7 py-6 text-xs font-bold uppercase tracking-[0.13em] text-white hover:bg-purple-700" data-testid="personal-loans-mid-cta">
                See What You Can Get Approved For
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="personal-loans-secured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="mb-4 text-3xl font-bold text-[#0F0A1A] sm:text-4xl" data-testid="personal-loans-secured-heading">Secured vs Unsecured Personal Loans</h2>
          <p className="text-sm leading-relaxed text-[#4A4556] sm:text-base" data-testid="personal-loans-secured-text">
            Secured loans may provide lower rates when backed by an asset, while unsecured loans can offer faster access without collateral.
            We help you compare both structures based on your borrowing goals and risk profile.
          </p>
        </div>
      </section>

      <section className="py-16" data-testid="personal-loans-benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="mb-8 text-3xl font-bold text-[#0F0A1A] sm:text-4xl" data-testid="personal-loans-benefits-heading">Finance Options If You've Been Declined Before</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {BENEFITS.map((item) => (
              <article key={item.title} className="rounded-2xl border border-purple-200 bg-white p-6" data-testid={`personal-loans-benefit-card-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <item.icon size={19} className="mb-3 text-purple-700" />
                <h3 className="mb-2 font-semibold text-[#1B1431]" data-testid={`personal-loans-benefit-title-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{item.title}</h3>
                <p className="text-sm text-[#4A4556]" data-testid={`personal-loans-benefit-text-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16" data-testid="personal-loans-process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="mb-8 text-3xl font-bold text-[#0F0A1A] sm:text-4xl" data-testid="personal-loans-process-heading">How Flash Funding Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="personal-loans-process-grid">
            {PROCESS_STEPS.map((step, index) => (
              <article key={step} className="rounded-2xl border border-purple-200 bg-[#faf9fd] p-6" data-testid={`personal-loans-process-card-${index + 1}`}>
                <span className="mb-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-sm text-purple-700" data-testid={`personal-loans-process-number-${index + 1}`}>{index + 1}</span>
                <p className="text-sm text-[#4A4556]" data-testid={`personal-loans-process-text-${index + 1}`}>{step}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20" data-testid="personal-loans-final-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="mb-4 text-3xl font-bold text-[#0F0A1A] sm:text-4xl" data-testid="personal-loans-final-cta-heading">
            Check What You Can Get Approved For
          </h2>
          <p className="mb-8 text-sm text-[#4A4556] sm:text-base" data-testid="personal-loans-final-cta-subheading">
            Complete the guided form and we’ll help you compare lender options that fit your situation.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-testid="personal-loans-final-cta-row">
            <a href={PORTAL_LINKS.PERSONAL} data-testid="personal-loans-final-primary-link">
              <Button className="w-full rounded-full bg-purple-600 px-8 py-6 text-xs font-bold uppercase tracking-[0.14em] text-white hover:bg-purple-700 sm:w-auto" data-testid="personal-loans-final-primary-cta">
                Check Your Options
              </Button>
            </a>
            <a href={PORTAL_LINKS.PERSONAL} data-testid="personal-loans-final-secondary-link">
              <Button variant="outline" className="w-full rounded-full border-purple-300 bg-purple-50 px-8 py-6 text-xs font-bold uppercase tracking-[0.14em] text-purple-700 hover:bg-purple-100 sm:w-auto" data-testid="personal-loans-final-secondary-cta">
                See What You Can Get Approved For
              </Button>
            </a>
          </div>
          <p className="mt-4 text-xs text-[#665F79]" data-testid="personal-loans-final-note">
            <CheckCircle2 size={12} className="mr-1 inline text-purple-700" />
            Takes 60 seconds • No impact on your credit score
          </p>
        </div>
      </section>
    </div>
  );
}
